/**
 * StationInfo Component
 *
 * This component serves as the main container for displaying and managing station information.
 * It handles the fetching of station data and conditionally renders either the station information
 * display, an "Add New Station" button, or a loading state.
 *
 * Key Features:
 * - Fetches station data for a specific station using the station's UID
 * - Implements React Query for efficient data fetching and caching
 * - Provides a user-friendly interface for adding new station information
 * - Handles loading and error states appropriately
 *
 * Data Flow:
 * 1. Component receives station UID from URL parameters
 * 2. Fetches station data using the UID
 * 3. Based on data availability, renders either:
 *    - StationInfoDisplay (if data exists)
 *    - AddStationDialog (if user chooses to add new station)
 *    - Empty state with "Add New" button
 */

import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { Typography, Button, CircularProgress, Box } from "@mui/material";
import { useQuery } from "react-query";
import { getFormData } from "../../../services/api";
import StationInfoDisplay from "./StationInfoDisplay";
import StationInfoActions from "./StationInfoActions";
import AddStationDialog from "./AddStationDialog";

const StationInfo = () => {
  // Get station UID from URL parameters
  const { uid } = useParams();

  // State to control the visibility of the Add Station dialog
  const [showAddComponent, setShowAddComponent] = useState(false);

  // Fetch station data using React Query
  // Data is considered stale after 5 minutes and cache is cleared after 10 minutes
  const {
    data: stationData,
    isLoading,
    error,
  } = useQuery(
    ["stationInfo", uid],
    () => getFormData(`metadata/station-info/${uid}`),
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    }
  );

  // Function to handle adding new station data
  const addNewStation = (newStationData) => {
    // Logic to add new station data
    console.log("Adding new station:", newStationData);
  };

  // Function to handle station deletion
  const deleteStation = () => {
    // Logic to delete station data
    console.log("Deleting station with ID:", uid);
  };

  // Function to handle station editing
  const onEdit = () => {
    // Logic to handle edit action
    console.log("Editing station with ID:", uid);
  };

  // Show loading spinner while data is being fetched
  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  // Display error message if data fetching fails
  if (error) {
    return (
      <Typography color="error">
        Error loading station data: {error.message}
      </Typography>
    );
  }

  // Main render logic
  return (
    <div>
      {stationData && stationData.length > 0 ? (
        // Display existing station information
        <StationInfoDisplay stationData={stationData} />
      ) : showAddComponent ? (
        // Show Add Station dialog when user clicks "Add New"
        <AddStationDialog onAdd={() => setShowAddComponent(false)} />
      ) : (
        // Show empty state with "Add New" button
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          minHeight="200px"
        >
          <Typography variant="h6" color="textSecondary">
            No information available
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowAddComponent(true)}
          >
            Add New
          </Button>
        </Box>
      )}
    </div>
  );
};

export default StationInfo;
